/** カード履歴表: 列 ID と localStorage レイアウト（全カード共通・端末ごと） */

export type TppHistoryColumnId =
  | 'id'
  | 'eventKind'
  | 'outDate'
  | 'inDate'
  | 'borrowerName'
  | 'purpose'
  | 'route'
  | 'amount'
  | 'checkoutStatus'
  | 'proof'
  | 'edit';

/** 列ピッカーで選べる全列（順序の正） */
export const TPP_HISTORY_ALL_COLUMNS: TppHistoryColumnId[] = [
  'id',
  'eventKind',
  'outDate',
  'inDate',
  'borrowerName',
  'purpose',
  'route',
  'amount',
  'checkoutStatus',
  'proof',
  'edit',
];

/**
 * 初回の既定列（チャージ中心でも空の「用務・経路・証跡」を出さない）。
 * 用務・経路・証跡は「列の表示」から追加。
 */
export const TPP_HISTORY_DEFAULT_COLUMNS: TppHistoryColumnId[] = [
  'eventKind',
  'outDate',
  'inDate',
  'amount',
  'edit',
];

export const TPP_HISTORY_SORTABLE_COLUMNS: TppHistoryColumnId[] = [
  'id',
  'eventKind',
  'outDate',
  'inDate',
  'borrowerName',
  'purpose',
  'route',
  'amount',
  'checkoutStatus',
];

/** v3: 既定列から用務・経路・証跡を外した */
const STORAGE_KEY = 'tpp_history_table_columns_v3';

const ALL_COLUMN_SET = new Set<string>(TPP_HISTORY_ALL_COLUMNS);

function normalizeColumnLayout(raw: unknown): TppHistoryColumnId[] {
  if (!Array.isArray(raw)) {
    return [...TPP_HISTORY_DEFAULT_COLUMNS];
  }
  const seen = new Set<TppHistoryColumnId>();
  const result: TppHistoryColumnId[] = [];
  for (const item of raw) {
    if (typeof item !== 'string' || !ALL_COLUMN_SET.has(item)) {
      continue;
    }
    const col = item as TppHistoryColumnId;
    if (seen.has(col)) {
      continue;
    }
    seen.add(col);
    result.push(col);
  }
  // 保存済み配列を尊重（非表示にした列を既定で戻さない）
  return result.length > 0 ? result : [...TPP_HISTORY_DEFAULT_COLUMNS];
}

export function loadHistoryColumnLayout(): TppHistoryColumnId[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [...TPP_HISTORY_DEFAULT_COLUMNS];
    }
    return normalizeColumnLayout(JSON.parse(raw));
  } catch {
    return [...TPP_HISTORY_DEFAULT_COLUMNS];
  }
}

export function saveHistoryColumnLayout(columns: TppHistoryColumnId[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
  } catch {
    /* private mode / quota */
  }
}

/** 既定列（localStorage には書かない。保存は saveHistoryColumnLayout） */
export function defaultHistoryColumnLayout(): TppHistoryColumnId[] {
  return [...TPP_HISTORY_DEFAULT_COLUMNS];
}

export type TppUsageContextCols = {
  hasPurpose: boolean;
  hasRoute: boolean;
  /** 既存証跡があるときだけ列を足す（未返却の空アップロードは返却に任せる） */
  needsProofCol: boolean;
  /** 利用行があるときだけ返却日列を出す（チャージのみなら隠す） */
  needsInDate: boolean;
};

/**
 * 表示用: ページ上のデータに応じて列を足す／空列を落とす（保存レイアウトは変えない）。
 */
export function withUsageContextColumns(
  columns: TppHistoryColumnId[],
  ctx: TppUsageContextCols
): TppHistoryColumnId[] {
  let cols = ctx.needsInDate ? columns : columns.filter((c) => c !== 'inDate');
  const needed: TppHistoryColumnId[] = [];
  if (ctx.hasPurpose) needed.push('purpose');
  if (ctx.hasRoute) needed.push('route');
  if (ctx.needsProofCol) needed.push('proof');
  if (needed.length === 0) return cols;
  const missing = needed.filter((c) => !cols.includes(c));
  if (missing.length === 0) return cols;
  const amountIdx = cols.indexOf('amount');
  const insertAt = amountIdx >= 0 ? amountIdx : Math.max(0, cols.length - 1);
  return [...cols.slice(0, insertAt), ...missing, ...cols.slice(insertAt)];
}

export function historyColumnLabelKey(col: TppHistoryColumnId): string {
  switch (col) {
    case 'id':
      return 'tpp.history_col_id';
    case 'eventKind':
      return 'tpp.col_kind';
    case 'outDate':
      // 履歴は貸出・チャージ混在のため「日付」。貸出モーダルは tpp.col_out（貸出日）のまま
      return 'tpp.history_col_date';
    case 'inDate':
      return 'tpp.col_in';
    case 'borrowerName':
      return 'tpp.col_user';
    case 'purpose':
      // 利用の用務とチャージのメモを同じ列に載せる
      return 'tpp.col_purpose_or_note';
    case 'route':
      return 'tpp.col_route';
    case 'amount':
      return 'tpp.col_amount';
    case 'checkoutStatus':
      return 'tpp.col_status';
    case 'proof':
      return 'tpp.col_proof';
    case 'edit':
      return 'tpp.edit_row';
    default:
      return col;
  }
}
